import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@components/icons';
import { RailIcon, type RailIconName } from './rail_icons';
import Typography from '@components/common/typography';
import { routes } from '@const/routes';
import { useUserContext } from '@hooks/context/useUserContext';
import { useNotFound } from '@hooks/helpers/useNotFound';
import useClickOutside from '@hooks/helpers/useClickOutside';
import { useEnvironmentContext } from '@hooks/context/useEnvironmentContext';
import { EnvironmentProvider, type Environment } from '@context/environment_context';
import ActivityServiceProvider from '../../service_providers/activity_provider';
import TestModeBanner from './TestModeBanner';
import { getFirstLetter } from '@utils/index';
import { EUserType } from '@xeppt/xeppt-sdk/types';
import ErrorBoundary from '@components/error_boundary';
import DevErrorFallback from './DevErrorFallback';
import styles from './styles.module.scss';

type TNavItem = {
    label: string;
    to: string;
    icon: RailIconName;
};

type TNavGroup = {
    caption?: string;
    items: TNavItem[];
};

const navGroups: TNavGroup[] = [
    {
        items: [{ label: 'Home', to: routes.developers_home, icon: 'home' }]
    },
    {
        caption: 'Develop',
        items: [
            { label: 'API keys', to: routes.developers_api_keys, icon: 'api_keys' },
            { label: 'Webhooks', to: routes.developers_webhooks, icon: 'webhooks' },
            { label: 'Sandbox', to: routes.developers_sandbox, icon: 'sandbox' }
        ]
    },
    {
        caption: 'Money movement',
        items: [
            { label: 'Orders', to: routes.developers_orders, icon: 'orders' },
            { label: 'Sales', to: routes.developers_sales, icon: 'sales' },
            { label: 'Payments', to: routes.developers_payments, icon: 'payments' },
            { label: 'Payouts', to: routes.developers_payouts, icon: 'payouts' },
            { label: 'Gift cards', to: routes.developers_gift_cards, icon: 'gift_cards' }
        ]
    },
    {
        caption: 'Account',
        items: [{ label: 'Settings', to: routes.developers_settings, icon: 'settings' }]
    }
];

const DEV_DOCS_URL = 'https://docs.xeppt.com';

// Real Sandbox/Live switcher (dev-portal enterprise redesign spec §3.2
// Option B) — the topbar pill now reads/writes the shared EnvironmentContext
// instead of deriving a read-only label from whichever API key happened to
// load first.
const ENV_OPTIONS: { value: Environment; label: string }[] = [
    { value: 'test', label: 'Sandbox' },
    { value: 'live', label: 'Live mode' }
];

const DeveloperProviderContent = () => {
    const { user, handleLogout } = useUserContext();
    const { handleNotFound } = useNotFound();
    const navigate = useNavigate();
    const location = useLocation();
    const { env, setEnv } = useEnvironmentContext();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNavOpen, setIsNavOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!user) return;
        if (user.type !== EUserType.BUSINESS) {
            handleNotFound({ error: { statusCode: 404 }, isRedirectNotFound: true });
        }
    }, [user]);

    useEffect(() => {
        setIsMenuOpen(false);
        setIsNavOpen(false);
    }, [location]);

    useClickOutside({
        ref: menuRef,
        callback: () => setIsMenuOpen(false),
        event: 'mousedown'
    });

    const initials = useMemo(
        () =>
            `${getFirstLetter(user?.profile?.first_name)}${getFirstLetter(
                user?.profile?.last_name
            )}`.toUpperCase(),
        [user]
    );

    const fullName = [user?.profile?.first_name, user?.profile?.last_name]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={styles.wrapper}>
            <div
                className={[styles.scrim, isNavOpen ? styles.scrim_open : ''].join(' ')}
                onClick={() => setIsNavOpen(false)}
            />
            <aside className={[styles.sidebar, isNavOpen ? styles.sidebar_open : ''].join(' ')}>
                <div className={styles.brand}>
                    <span className={styles.wordmark}>
                        X<b>E</b>PPT
                    </span>
                    <span className={styles.brand_badge}>Dev</span>
                </div>
                <nav className={styles.nav}>
                    {navGroups.map((group, index) => (
                        <div key={index} className={styles.nav_group}>
                            {group.caption && <p className={styles.nav_caption}>{group.caption}</p>}
                            {group.items.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className={({ isActive }) =>
                                        [styles.nav_item, isActive ? styles.is_active : ''].join(' ')
                                    }>
                                    <RailIcon name={item.icon} />
                                    <Typography variant="body4">{item.label}</Typography>
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </nav>
                <div className={styles.sidebar_footer}>
                    <Link to={routes.dashboard} className={styles.back_link}>
                        <svg
                            width={15}
                            height={15}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.9}
                            aria-hidden="true">
                            <path d="M15 5l-7 7 7 7" />
                        </svg>
                        <Typography variant="body4">Back to XEPPT wallet</Typography>
                    </Link>
                </div>
            </aside>

            <div className={styles.main_column}>
                <TestModeBanner />
                <header className={styles.topbar}>
                    <div className={styles.topbar_left}>
                        <button
                            className={styles.hamburger}
                            onClick={() => setIsNavOpen((state) => !state)}
                            aria-label="Toggle navigation">
                            <span />
                            <span />
                            <span />
                        </button>
                        <div
                            className={styles.env_pill_wrapper}
                            role="tablist"
                            aria-label="API environment">
                            {ENV_OPTIONS.map((opt) => {
                                const isSelected = env === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        role="tab"
                                        aria-selected={isSelected}
                                        className={[
                                            styles.env_pill_option,
                                            isSelected ? styles.env_pill : '',
                                            isSelected
                                                ? opt.value === 'live'
                                                    ? styles.env_live
                                                    : styles.env_test
                                                : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={() => setEnv(opt.value)}>
                                        <span className={styles.env_dot} />
                                        <span className={styles.env_label}>{opt.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.topbar_right}>
                        <a
                            href={DEV_DOCS_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.docs_link}>
                            <Icon name="document" width={16} height={16} />
                            <span>Docs</span>
                            <Icon name="external_link" width={13} height={13} />
                        </a>
                        <span className={styles.divider_v} />
                        <div className={styles.account} ref={menuRef}>
                            <button
                                className={styles.account_button}
                                onClick={() => setIsMenuOpen((state) => !state)}>
                                {user?.profile?.avatar ? (
                                    <img
                                        className={styles.avatar}
                                        src={user?.profile?.avatar}
                                        alt={`${fullName || 'Business'} avatar`}
                                    />
                                ) : (
                                    <span className={styles.avatar}>{initials}</span>
                                )}
                                <span className={styles.account_name}>
                                    {fullName || user?.email}
                                </span>
                                <Icon
                                    name="nav_down"
                                    width={16}
                                    height={16}
                                    className={[
                                        styles.account_chevron,
                                        isMenuOpen ? styles.account_chevron_open : ''
                                    ].join(' ')}
                                />
                            </button>
                            {isMenuOpen && (
                                <div className={styles.account_menu}>
                                    <div className={styles.account_menu_head}>
                                        {user?.profile?.avatar ? (
                                            <img
                                                className={styles.avatar_lg}
                                                src={user?.profile?.avatar}
                                                alt={`${fullName || 'Business'} avatar`}
                                            />
                                        ) : (
                                            <span className={styles.avatar_lg}>{initials}</span>
                                        )}
                                        <div className={styles.account_menu_id}>
                                            <Typography variant="body4" weight="semibold">
                                                {fullName || 'XEPPT Business'}
                                            </Typography>
                                            <p className={styles.account_email}>{user?.email}</p>
                                        </div>
                                    </div>
                                    <div className={styles.account_menu_body}>
                                        <button
                                            className={styles.menu_item}
                                            onClick={() => navigate(routes.developers_settings)}>
                                            <Icon name="settings" width={18} height={18} />
                                            <Typography variant="body4">
                                                Developer settings
                                            </Typography>
                                        </button>
                                        <button
                                            className={styles.menu_item}
                                            onClick={() => navigate(routes.dashboard)}>
                                            <Icon name="outlined_wallet" width={18} height={18} />
                                            <Typography variant="body4">
                                                Back to XEPPT wallet
                                            </Typography>
                                        </button>
                                    </div>
                                    <div className={styles.account_menu_foot}>
                                        <button
                                            className={[styles.menu_item, styles.menu_danger].join(
                                                ' '
                                            )}
                                            onClick={() => handleLogout()}>
                                            <Icon name="logout" width={18} height={18} />
                                            <Typography variant="body4">Log out</Typography>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <main className={styles.content}>
                    {/* Keyed by route so navigating to a different dev-portal
                        page always remounts a fresh boundary — a crash on one
                        page never leaves stale error state behind on the next.
                        Contained here (not around the whole shell above) so a
                        crash in the routed page never takes the sidebar/topbar
                        nav down with it — req'd so the rest of the dev portal
                        stays usable. */}
                    <ErrorBoundary key={location.pathname} fallback={DevErrorFallback}>
                        <Outlet />
                    </ErrorBoundary>
                </main>
            </div>
        </div>
    );
};

// EnvironmentProvider is mounted here (rather than higher up in the router)
// so it wraps exactly the dev-portal shell — the topbar pill above and every
// /developers/* page rendered through <Outlet/> below — and nothing outside
// the dev portal. DeveloperProviderContent must stay a separate component:
// it calls useEnvironmentContext() to read/write the pill's state, which
// only resolves once it's rendered as a *descendant* of the provider, not a
// sibling defined in the same function body.
//
// ActivityServiceProvider gives the dev portal the SAME 15-minute inactivity
// auto-logout (and the 14:30 warning modal) that the wallet shell mounts in
// global_provider. Without it, /developers/* was an escape hatch with no idle
// timeout — a session could stay alive there indefinitely. Both shells key off
// the shared localStorage 'lastActivityTime', and only one shell is mounted per
// route (they are sibling routes), so the idle clock stays continuous across
// wallet↔dev navigation with no duplicate timers.
const DeveloperProvider = () => (
    <EnvironmentProvider>
        <ActivityServiceProvider>
            <DeveloperProviderContent />
        </ActivityServiceProvider>
    </EnvironmentProvider>
);

export default DeveloperProvider;
